import { useEffect, useRef, useState } from "react";
import { ChevronDownIcon, XMarkIcon } from "@heroicons/react/24/solid";
import AiSpinner from "./AiSpinner";
import NoteTagEditor from "./NoteTagEditor";
import SuggestionIcon from "./SuggestionIcon";
import SuggestionStatusBadge from "./SuggestionStatusBadge";
import ActionStepTimeline from "./ActionStepTimeline";
import TaggedUsers from "@components/TaggedUsers";
import { withoutSmsForStatements } from "@areas/trade-accounts/flows/BatchTemplateRequestModal/utils/buildSuggestionRequest";
import { sendingToContacts } from "./actionAmendments";
import { withScheduledSendTime } from "./actionSteps";
import { cancellationKind, cancellationText } from "./cancellationCopy";
import { paymentSummary } from "./suggestedPaymentCopy";
import useActionStepsDisclosure from "./useActionStepsDisclosure";
import useActionTimeline from "./useActionTimeline";
import useTypewriter from "./useTypewriter";

// The model should emit these selectors as arrays, but occasionally returns a bare string;
// coerce defensively so the card never throws on .join / .map.
function toArray(value) {
  if (Array.isArray(value)) return value;
  if (value == null || value === "") return [];
  return [value];
}

// An email to the customer — plain, or carrying documents. The legacy name is what cards and
// replies persisted before the intelligence-service rename still carry; drop it once nothing
// emits it any more.
export const SEND_EMAIL_TYPE = "SendEmailRequested";
export const LEGACY_SEND_EMAIL_TYPE = "SendDocumentsRequested";
export const isSendEmailType = (type) =>
  type === SEND_EMAIL_TYPE || type === LEGACY_SEND_EMAIL_TYPE;

// Signal types carried out by the action-service as pre-created multi-step work (steps,
// actionId, accept/reject decision URLs). Grows as action-backed signal types are added.
export const ACTION_BACKED_TYPES = [
  "PromiseToPay",
  "PaymentPlan",
  "PaymentRequestRequested",
  SEND_EMAIL_TYPE,
  LEGACY_SEND_EMAIL_TYPE,
];
export const isActionBacked = (type) => ACTION_BACKED_TYPES.includes(type);

// A cancellation targets EXISTING work (extracted.targetActionId) rather than creating any —
// its Confirm posts to the cancel endpoint, and its dismiss is client-side only (there is no
// pre-created action of its own to reject).
export const isCancellation = (type) => type === "CancellationRequested";

// An email card keeps the label the server sent — "Send email" for a plain message, "Send documents"
// once attachments ride along — so it is deliberately absent here.
const ACTION_LABELS = {
  CustomerInteractionNoted: "Add note",
};

const BUTTON_LABELS = {
  PaymentRequestRequested: "Send",
  CustomerInteractionNoted: "Add",
  [SEND_EMAIL_TYPE]: "Send",
  [LEGACY_SEND_EMAIL_TYPE]: "Send",
  PromiseToPay: "Create",
  PaymentPlan: "Create",
  CancellationRequested: "Confirm",
};

// The contact(s) the request will be sent to — the enabled channels resolved against the
// customer's actual email / mobile (e.g. "andrew@onti.io & 07404314999").
function contactList(config, customer) {
  return [
    config?.emailEnabled && customer?.email,
    config?.smsEnabled && customer?.phone,
  ]
    .filter(Boolean)
    .join(" & ");
}

function bodyText(type, params, customer, config) {
  if (type === "CustomerInteractionNoted") return params?.summary ?? "";
  if (isActionBacked(type)) return params?.explanation ?? "";
  if (isSendEmailType(type)) {
    const to = contactList(config, customer);
    const suffix = to ? ` to ${to}` : "";
    const invoiceNums = toArray(params?.invoiceNumbers);
    const salesOrderNums = toArray(params?.salesOrderNumbers);
    const invoices = invoiceNums.length
      ? ` for invoice ${invoiceNums.join(", ")}`
      : "";
    const salesOrders = salesOrderNums.length
      ? ` for sales order ${salesOrderNums.join(", ")}`
      : "";
    const document =
      params?.documentType === "Invoice" ? "invoice" : "proof of delivery";
    return `Send ${document}${invoices || salesOrders}${suffix}`;
  }
  return "";
}

// The "Ontik suggests" card shown once draft analysis returns a suggestion. It
// animates in (staggered by `index`) and types its body out for an AI feel.
// `onAccept` commits the action; the corner dismiss (X) rejects a still-decidable Action
// suggestion via `onReject`, or otherwise just clears the card from view; `isSending`
// shows a loader and locks the card while the request is in flight. An editable action
// (a single payment.request.send or email step) offers `onEdit`; there is no inline
// Reject button — rejection is only via the corner X.
export default function DraftSuggestionCard({
  suggestion,
  index = 0,
  onAccept,
  onReject,
  onEdit,
  isSending = false,
  isDone = false,
  isRejected = false,
  taggedUsers = [],
  customer,
  onGrow,
  scrollContainerRef,
  onPreviewStep,
}) {
  const isNote = suggestion.type === "CustomerInteractionNoted";
  const isSendEmail = isSendEmailType(suggestion.type);
  const isCancel = isCancellation(suggestion.type);
  const isAction = isActionBacked(suggestion.type);
  // A merchant-scheduled payment request shares PromiseToPay's signal type (see useSignalChat's
  // alias), so it reads params.kind rather than type to tell the two apart.
  const isScheduleKind =
    suggestion.type === "PromiseToPay" && suggestion.params?.kind === "schedule";
  const action = isCancel
    ? `Cancel ${cancellationKind(suggestion.params)}`
    : isScheduleKind
      ? "Scheduled payment request"
      : (ACTION_LABELS[suggestion.type] ?? suggestion.label);
  const buttonLabel = BUTTON_LABELS[suggestion.type] ?? "Send";

  // Set when the user hits Send before the action id has streamed in: holds their intent so it can
  // fire the instant the id lands (see the deferred-accept effect), with the normal sending spinner
  // in the meantime — the "still preparing" wait is never surfaced.
  const [wantsAccept, setWantsAccept] = useState(null);
  // Mirrors wantsAccept for the Edit button: set when Edit is pressed before the action's id has landed, so
  // the editor opens the instant it does (see the deferred-edit effect) — Edit never has to pop in late.
  const [wantsEdit, setWantsEdit] = useState(false);

  // Statements can't be delivered by SMS — drop it as a comm method (email only).
  const config = withoutSmsForStatements(suggestion.params?.requestConfig);
  const actionTimeline = useActionTimeline(
    suggestion.params?.steps,
    suggestion.params?.explanation,
    suggestion.type,
    suggestion.params?.kind,
  );
  // A cancellation mirrors the work it cancels: the extractor stamps a compact "£200 weekly ·
  // 2x payments · Starting 21 Aug 2026" summary (cardSummary) in the same shape the original
  // suggestion card showed. Fall back to the plain "Cancel the …" sentence if it's absent.
  const baseBody = isAction
    ? actionTimeline.summary
    : isCancel
      ? (suggestion.params?.cardSummary ?? cancellationText(suggestion.params))
      : bodyText(suggestion.type, suggestion.params, customer, config);
  // A promise-to-pay description ends with the promised date; show the schedule's own send time so
  // it reads "£300 · Thu, 20 Aug 2026 at 7am". Idempotent — replaces any time the model already
  // wrote into the label so it never appears twice.
  const derivedBody =
    suggestion.type === "PromiseToPay"
      ? withScheduledSendTime(baseBody, actionTimeline.groups)
      : baseBody;
  // A payment suggestion reads its copy from the facts the service resolved (amount, what it
  // targets, when it sends); the derived body is the fallback for anything it can't describe.
  const body = paymentSummary(suggestion.payment) ?? derivedBody;
  // The card's content appears complete the moment the card does — no per-card type-out, which made the
  // body crawl in and then the "Sending to:" line type in as a second stage after it (reading as the
  // card "coming in later" and rendering twice). The reply beside it fades in; the card matches.
  const typed = useTypewriter(body, { enabled: false });
  const complete = typed === body;

  // The proposed recipient(s), shown as a small line beneath the description. Card-level only — the step
  // timeline never shows contacts.
  const sendingTo = sendingToContacts(suggestion.params);
  const sendingToText = sendingTo
    ? `Sending to: ${[
        sendingTo.email,
        sendingTo.phone,
        sendingTo.cc ? `Cc: ${sendingTo.cc}` : null,
        sendingTo.bcc ? `Bcc: ${sendingTo.bcc}` : null,
      ]
        .filter(Boolean)
        .join(" · ")}`
    : null;
  const typedSendingTo = useTypewriter(sendingToText ?? "", { enabled: false });

  const actionId = suggestion.params?.actionId;
  // The action's card has streamed in but its id hasn't landed yet — a transient ~1-2s wait. The card
  // deliberately looks and behaves as ready (the accept is queued, below), so this is never surfaced.
  const preparing = isAction && !actionId && suggestion.preparing === true;
  const expired =
    Boolean(suggestion.params?.expiresAt) &&
    new Date(suggestion.params.expiresAt) <= new Date();
  // Undecidable = genuinely can't act: the turn finished with no action (failed to prepare), or the
  // approval window lapsed. A still-preparing action is NOT undecidable — Send is queued until the id lands.
  const actionUndecidable = isAction && ((!actionId && !preparing) || expired);
  // A cancellation card is only confirmable when the extractor resolved a single target.
  const cancellationUndecidable = isCancel && !suggestion.params?.targetActionId;
  // Editable: a single payment.request.send step (an instant request or a promise to
  // pay — never a plan's multiple instalments) whose details the user can tweak before
  // accepting, via the Edit button. The corner X still rejects. Offered on an editable KIND alone (a single
  // email.send / payment.request.send with a payload), so Edit is present from first paint like Send — the
  // step's id lands with pre-creation, and a click before then is queued (wantsEdit) and opens once ready.
  const editableStep = (suggestion.params?.steps ?? []).filter(
    (step) => step?.stepType === "payment.request.send" || step?.stepType === "email.send",
  );
  const isEditable =
    isAction && !actionUndecidable && !!onEdit && editableStep.length === 1 && !!editableStep[0]?.payload;
  // An email suggestion (plain or with documents) is delivered by email only, so it must be enabled.
  const sendDisabled =
    isSending ||
    Boolean(wantsAccept) ||
    (isSendEmail && !config?.emailEnabled) ||
    actionUndecidable ||
    cancellationUndecidable;

  // Why the card can't be sent, shown inline (not just on button hover) — only for a genuine block:
  // failed to prepare, expired, or no email on file. A still-preparing action shows nothing: it looks
  // ready and the accept is queued, so the user never sees the wait.
  const disabledReason = actionUndecidable
    ? actionId
      ? "This suggestion has expired — ask again in the chat for a fresh one."
      : "This suggestion couldn't be prepared — ask again in the chat."
    : cancellationUndecidable
      ? "This couldn't be resolved to one item — say which one in the chat."
      : isSendEmail && !config?.emailEnabled
        ? "This customer has no email on file, so the email can't be sent."
        : null;

  // A single step reads fine from the card body alone, so only surface the timeline
  // (and its expand/collapse toggle) once there's more than one row to show.
  const showTimeline = isAction && actionTimeline.groups.length > 1;

  // A note has no action to amend, so it edits inline here (rather than via the registry-driven
  // card): Edit swaps the quoted body for a textarea seeded with the note text, and Add commits the
  // edited content through the same handleSave path as an unedited note.
  const [editing, setEditing] = useState(false);
  const [noteDraft, setNoteDraft] = useState("");
  // null until the note is edited, then holds the edited tag list; the display falls back to the
  // suggestion's original tags (taggedUsers) while unedited.
  const [noteTags, setNoteTags] = useState(null);

  // A card with no step timeline grows after mount — the body types out and the
  // "Sending to:" line only appears once it's done — so keep the thread pinned to the
  // bottom as it does. Timeline cards position themselves via useActionStepsDisclosure.
  useEffect(() => {
    if (!showTimeline) onGrow?.();
  }, [typed, typedSendingTo, showTimeline, editing, onGrow]);

  // onAccept/onEdit are fresh closures on every parent render, and accepting re-renders the parent
  // (it marks the card pending). Depending on them in the deferred effects below would re-run the
  // effect on that re-render and fire the accept again — an unbounded setState loop. Hold them in refs
  // so the effects fire once, keyed only off the queued intent and the action's readiness.
  const onAcceptRef = useRef(onAccept);
  onAcceptRef.current = onAccept;
  const onEditRef = useRef(onEdit);
  onEditRef.current = onEdit;

  // Deferred accept: if Send was pressed while the action was still preparing, fire it the instant the
  // id lands (onAccept closes over the now-resolved suggestion). If the turn instead finishes with no
  // action, drop the intent — the card falls back to its genuine "couldn't be prepared" state.
  useEffect(() => {
    if (!wantsAccept) return;
    if (actionId) {
      onAcceptRef.current(wantsAccept.content, wantsAccept.mentions);
      setWantsAccept(null);
    } else if (!preparing) {
      setWantsAccept(null);
    }
  }, [wantsAccept, actionId, preparing]);

  // Deferred edit: if Edit was pressed while the action was still preparing, open the editor the instant the
  // id lands (onEdit closes over the now-resolved suggestion, so the modal seeds from the ready step). If the
  // turn instead finishes with no action, drop the intent.
  useEffect(() => {
    if (!wantsEdit) return;
    if (actionId) {
      onEditRef.current();
      setWantsEdit(false);
    } else if (!preparing) {
      setWantsEdit(false);
    }
  }, [wantsEdit, actionId, preparing]);

  const cardRef = useRef(null);
  const stepsDisclosure = useActionStepsDisclosure({
    enabled: showTimeline,
    cardRef,
    scrollContainerRef,
    collapseOn: isSending || isDone || isRejected,
  });

  const isDecided = isDone || isRejected;
  // The inline editor is only shown while actively editing; sending (in flight) and decided both
  // collapse it back to the quoted body so accept lands on a stable state, not a lingering field.
  // A failed save leaves `editing` on, so once the in-flight state clears the editor returns to retry.
  const showNoteEditor = isNote && editing && !isSending && !isDecided;
  // Once edited, the quoted body reflects the edited draft (the original summary never changes),
  // shown without the typewriter since it has already been read.
  const noteText = noteDraft || typed;
  // The tags shown/committed: the edited list once edited, else the suggestion's original tags.
  const displayTags = noteTags ?? taggedUsers;
  // The corner X (only shown while the suggestion is undecided) either cancels an in-progress note
  // edit — returning the card to its initial state — or rejects the suggestion so the accept button
  // flips to a crossed badge, matching the promise-to-pay / payment-plan flow. Once decided the X is
  // gone and the card stays put.
  const handleDismiss = () => {
    if (editing) {
      setEditing(false);
      setNoteDraft("");
      setNoteTags(null);
      return;
    }
    onReject?.();
  };

  // Hollow tile: gradient outline with a gradient sparkle on a white fill.
  const glyph = (
    <span className="shrink-0 rounded-xl bg-[linear-gradient(135deg,#ec4899,#a855f7,#3b82f6,#22d3ee,#fb923c)] p-px">
      <span className="flex size-9 items-center justify-center rounded-[11px] bg-white">
        <SuggestionIcon type={suggestion.type} kind={suggestion.params?.kind} className="size-5" />
      </span>
    </span>
  );

  const acceptButton = (
    <button
      type="button"
      onClick={() => {
        const content = isNote && editing ? noteDraft : undefined;
        const mentions = isNote && editing ? displayTags : undefined;
        // Queue the accept while the action is still preparing; fire it now once it's ready.
        if (preparing) setWantsAccept({ content, mentions });
        else onAccept(content, mentions);
      }}
      disabled={sendDisabled || (isNote && editing && !noteDraft.trim())}
      title={!isSending && disabledReason ? disabledReason : undefined}
      className={`flex min-w-[2.5rem] items-center justify-center rounded text-sm font-semibold focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600 disabled:cursor-not-allowed disabled:text-zinc-300 ${
        isCancel ? "text-red-600 hover:text-red-500" : "text-zinc-900 hover:text-zinc-600"
      }`}
    >
      {isSending || wantsAccept ? <AiSpinner size={18} /> : buttonLabel}
    </button>
  );

  return (
    <div
      ref={cardRef}
      className="animate-fadeIn relative flex flex-col gap-4 rounded-2xl border border-zinc-200 bg-white pt-6 px-4 pb-4 shadow-sm"
      style={{ animationDelay: `${index * 90}ms` }}
    >
      {/* Same corner treatment as the expand/collapse toggle (mirrored to the top edge). Hidden once
          decided so a rejected/accepted card can't be cleared away — it always stays. */}
      {!isDecided && (
        <button
          type="button"
          onClick={handleDismiss}
          disabled={isSending || Boolean(wantsAccept)}
          aria-label="Dismiss suggestion"
          className="absolute right-4 top-3 text-zinc-400 hover:text-zinc-600 disabled:cursor-not-allowed disabled:opacity-40"
        >
          <XMarkIcon aria-hidden="true" className="size-3.5" />
        </button>
      )}

      {showNoteEditor ? (
        /* Edit: mirrors the task card — logo + label + action button in a top row, the editable
           note text and tags stacked below full-width (rather than a single vertically-centred row). */
        <div className="flex flex-col gap-2.5">
          <div className="flex items-center gap-3">
            {glyph}
            <div className="min-w-0 flex-1 text-xs text-zinc-500">{action}</div>
            {acceptButton}
          </div>
          <textarea
            rows={3}
            value={noteDraft}
            aria-label="Edit note"
            // eslint-disable-next-line jsx-a11y/no-autofocus
            autoFocus
            onChange={(e) => setNoteDraft(e.target.value)}
            className="w-full rounded-lg border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 placeholder:text-zinc-400 focus:outline focus:outline-1 focus:-outline-offset-1 focus:outline-primary disabled:bg-zinc-50 disabled:text-zinc-400"
          />
          <NoteTagEditor users={displayTags} onChange={setNoteTags} onLayoutChange={onGrow} />
        </div>
      ) : (
        <div className="flex items-center gap-3">
          {glyph}

          <div className="min-w-0 flex-1">
            <div className="text-xs text-zinc-500">{action}</div>
            {body && (
              <div
                className={
                  isNote
                    ? "mt-0.5 text-sm text-zinc-900"
                    : "mt-0.5 text-sm text-zinc-600"
                }
              >
                {isNote ? `“${noteText}${noteDraft || complete ? "”" : ""}` : typed}
              </div>
            )}
            {complete && sendingToText && (
              <div className="mt-1 text-xs text-zinc-400">{typedSendingTo}</div>
            )}
            {!isDone && !isRejected && disabledReason && (
              <div className="mt-1 text-xs text-amber-600">{disabledReason}</div>
            )}
            {isNote && <TaggedUsers users={displayTags} />}
          </div>

          {isDone || isRejected ? (
            <SuggestionStatusBadge done={isDone} />
          ) : (
            <>
              {isEditable && (
                <button
                  type="button"
                  // Open now if the action is ready; otherwise queue and open the instant its id lands — like Send.
                  onClick={() => (actionId ? onEdit() : setWantsEdit(true))}
                  disabled={sendDisabled || wantsEdit}
                  className="flex min-w-[2.5rem] items-center justify-center rounded text-sm font-semibold text-zinc-900 hover:text-zinc-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600 disabled:cursor-not-allowed disabled:text-zinc-300"
                >
                  {wantsEdit ? <AiSpinner size={18} /> : "Edit"}
                </button>
              )}
              {isNote && !editing && !isSending && (
                <button
                  type="button"
                  onClick={() => {
                    setNoteDraft(body);
                    setNoteTags(taggedUsers);
                    setEditing(true);
                  }}
                  className="flex items-center justify-center rounded text-sm font-semibold text-zinc-900 hover:text-zinc-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600 disabled:cursor-not-allowed disabled:text-zinc-300"
                >
                  Edit
                </button>
              )}
              {acceptButton}
            </>
          )}
        </div>
      )}

      {showTimeline && (
        <ActionStepTimeline
          groups={actionTimeline.groups}
          expanded={stepsDisclosure.expanded}
          instant={stepsDisclosure.instant}
          onPreviewStep={onPreviewStep}
        />
      )}

      {showTimeline && (
        <button
          type="button"
          onClick={stepsDisclosure.toggle}
          className="absolute bottom-3 right-4 flex items-center gap-1 text-xs font-medium text-zinc-400 hover:text-zinc-600"
        >
          {stepsDisclosure.expanded ? "Collapse" : "Expand"}
          <ChevronDownIcon
            aria-hidden="true"
            className={`size-3.5 transition-transform duration-200 ${
              stepsDisclosure.expanded ? "rotate-180" : ""
            }`}
          />
        </button>
      )}
    </div>
  );
}
